import { useEffect } from 'react';

import { listenForChildMessage, postToOpener } from 'components/CallScribe/popupChannel';
import { digitsOf } from 'components/CallScribe/personMatch';

const LIVE_CALL_MESSAGE = 'mspp.callscribe.liveCall';

export interface ISoftphoneLiveCall {
  toNumber: string;
  callee: string;
}

const isLiveCall = (value: unknown): value is ISoftphoneLiveCall => {
  const live = value as Partial<ISoftphoneLiveCall> | null;
  return typeof live?.toNumber === 'string' && typeof live.callee === 'string';
};

const reportLiveCall = (live: ISoftphoneLiveCall | null): void => {
  postToOpener({ type: LIVE_CALL_MESSAGE, live });
};

export const useReportLiveCall = (live: ISoftphoneLiveCall | null): void => {
  const toNumber = live?.toNumber ?? null;
  const callee = live?.callee ?? '';

  useEffect(() => {
    reportLiveCall(toNumber === null ? null : { toNumber, callee });
  }, [toNumber, callee]);

  useEffect(() => {
    const onPageHide = (): void => { reportLiveCall(null); };
    window.addEventListener('pagehide', onPageHide);
    return () => { window.removeEventListener('pagehide', onPageHide); };
  }, []);
};

const reports = new WeakMap<object, ISoftphoneLiveCall | null>();
let watching = false;

export const watchLiveCalls = (): void => {
  if (watching) return;
  watching = true;
  listenForChildMessage(LIVE_CALL_MESSAGE, (data, source) => {
    if (!source || typeof source !== 'object') return;
    reports.set(source, isLiveCall(data.live) ? data.live : null);
  });
};

export const hasReportedIn = (popup: Window): boolean => reports.has(popup);

export const liveCallIn = (popup: Window | null): ISoftphoneLiveCall | null => {
  if (!popup) return null;
  try {
    if (popup.closed) return null;
  } catch {
    return null;
  }
  return reports.get(popup) ?? null;
};

export const isSameNumber = (a: string, b: string): boolean => {
  const left = digitsOf(a);
  return left.length > 0 && left === digitsOf(b);
};
